import { useState, useMemo, useCallback, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Button from '@/components/Button';
import { galleryItems, GALLERY_FILTERS } from '@/data/gallery';

const PAGE = 12;
const NAV_BTN =
  'absolute top-1/2 -translate-y-1/2 p-3 text-white bg-ink/60 hover:bg-ink transition-colors';

function Lightbox({ items, index, onChange }) {
  const open = index !== null;
  const item = open ? items[index] : null;
  const go = useCallback(
    (step) => onChange((index + step + items.length) % items.length),
    [index, items.length, onChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, go]);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onChange(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="on-dark fixed inset-0 z-[60] bg-ink/95" />
        <Dialog.Content
          className="on-dark fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-12 focus:outline-none"
          aria-describedby={undefined}
        >
          <Dialog.Title className="sr-only">{item?.alt}</Dialog.Title>
          {item && (
            <figure className="flex max-h-full max-w-full flex-col items-center">
              <img src={item.large} alt={item.alt} className="max-h-[80vh] max-w-full object-contain" />
              <figcaption className="mt-4 text-sm text-white/75">
                {item.categoryLabel} · {index + 1} / {items.length}
              </figcaption>
            </figure>
          )}
          <button type="button" onClick={() => go(-1)} aria-label="Prethodna fotografija" className={`${NAV_BTN} left-2 sm:left-4`}>
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Sljedeća fotografija" className={`${NAV_BTN} right-2 sm:right-4`}>
            <ChevronRight className="h-6 w-6" />
          </button>
          <Dialog.Close aria-label="Zatvori" className="absolute right-3 top-3 p-3 text-white bg-ink/60 hover:bg-ink transition-colors sm:right-5 sm:top-5">
            <X className="h-6 w-6" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function GalleryFilters({ filter, onChange, className = '' }) {
  return (
    <div role="group" aria-label="Filter radova po kategoriji" className={`flex flex-wrap gap-2 ${className}`}>
      {GALLERY_FILTERS.map((f) => (
        <button
          key={f.id}
          type="button"
          aria-pressed={filter === f.id}
          onClick={() => onChange(f.id)}
          className={`px-4 py-2 text-sm font-medium border transition-colors ${
            filter === f.id
              ? 'bg-white text-ink border-white'
              : 'border-white/30 text-white/85 hover:border-white hover:text-white'
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

// Roditelj postavlja key={filter} da se broj prikazanih slika vrati na početak pri promjeni filtera.
export default function Gallery({ filter, featuredShown = false, className = 'mt-16' }) {
  const [visible, setVisible] = useState(PAGE);
  const [active, setActive] = useState(null);

  const items = useMemo(() => galleryItems({ filter, featuredShown }), [filter, featuredShown]);

  return (
    <div className={className} data-testid="gallery">
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.slice(0, visible).map((g, i) => (
          <li key={g.id}>
            <button
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Uvećaj: ${g.alt}`}
              className="group block aspect-square w-full overflow-hidden bg-white/5"
            >
              <img
                src={g.thumb}
                alt={g.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
              />
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-8 flex flex-wrap items-center gap-4">
        {visible < items.length && (
          <Button variant="outlineLight" type="button" onClick={() => setVisible((v) => v + PAGE)} data-testid="gallery-more">
            Prikaži više radova
          </Button>
        )}
        <span className="text-sm text-white/60">
          Prikazano {Math.min(visible, items.length)} od {items.length}
        </span>
      </div>

      <Lightbox items={items} index={active} onChange={setActive} />
    </div>
  );
}
